import * as React from 'react';
import styled from 'styled-components';
import { IMenuItem, getCommonMenuStyle } from '../common';
import { FlyoutMenuItem } from './flyout-menu-item';
import { IFlyoutMenuProps } from './flyout-menu-types';
import { useHierarchicalData } from '../../../hooks';

const FlyoutMenuBase = React.memo((props: IFlyoutMenuProps) => {
    const {
        className, elementRef, items, defaultSelectedId, selectedId: selectedIdFromProps,
        highlightSelectionPath, onChange, onRenderItem, triggerChangeOnItemWithChildren, cursorType
    } = props;
    const {
        hasChildren, getChildItemsByParentId, getItemPathById,
        isExpanded, setSelectedId, shouldHighlightItem, setExpandedItems
    } = useHierarchicalData({
        items,
        defaultSelectedId,
        selectedId: selectedIdFromProps
    });

    const rootItems = React.useMemo(() => getChildItemsByParentId(), [getChildItemsByParentId]);


    const _getSubMenuItems = React.useCallback((id: string) => (isExpanded(id) ? getChildItemsByParentId(id) : []), [getChildItemsByParentId, isExpanded]);

    const _resetExpandedItems = React.useCallback(() => {
        setExpandedItems(['']);
    }, [setExpandedItems]);

    React.useEffect(_resetExpandedItems, [_resetExpandedItems]);

    const _onItemHovered = React.useCallback((id: string) => {
        setExpandedItems(getItemPathById(id));
    }, [getItemPathById, setExpandedItems]);

    const _onSelect = React.useCallback((id: string) => {
        if (!triggerChangeOnItemWithChildren && hasChildren(id)) {
            return;
        }
        _resetExpandedItems();
        setSelectedId(id);
        if (onChange === undefined) {
            return;
        }
        onChange(id);
    }, [triggerChangeOnItemWithChildren, hasChildren, _resetExpandedItems, setSelectedId, onChange]);

    const prepareItem = React.useCallback((item: IMenuItem) => (
        <FlyoutMenuItem
            hasChildren={hasChildren(item.id)}
            cursorType={cursorType}
            shouldRenderSubMenuItems={isExpanded(item.id)}
            itemInfo={item}
            onItemHovered={_onItemHovered}
            key={item.id}
            triggerChangeOnItemWithChildren={triggerChangeOnItemWithChildren}
            highlightSelectionPath={highlightSelectionPath}
            isItemSelected={shouldHighlightItem(item.id)}
            menuType="flyoutMenu"
            onRenderItem={onRenderItem}
            onSelect={_onSelect}
            getSubMenuItems={_getSubMenuItems}
            prepareItemToRender={prepareItem}
            isItemHovered={isExpanded(item.id)} />
    ), [hasChildren, cursorType, isExpanded, _onItemHovered, triggerChangeOnItemWithChildren, highlightSelectionPath, shouldHighlightItem, onRenderItem, _onSelect, _getSubMenuItems]);

    return (
        <div ref={elementRef}
            className={className}
            onMouseLeave={_resetExpandedItems}>
            {rootItems.map(prepareItem)}
        </div>
    );
});

FlyoutMenuBase.displayName = 'FlyoutMenuBase';

export const FlyoutMenu = styled(FlyoutMenuBase).attrs({ className: String('ap-flyoutMenu') })`
    display: inline-block;
    ${getCommonMenuStyle('flyoutMenu')}
`;

FlyoutMenu.displayName = 'FlyoutMenu';
